// src/pages/AnnoncesPage.js
// Page listing des annonces communautaires — route publique (acquisition SEO)
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Megaphone, Plus, MapPin, Search, Star, Zap } from 'lucide-react';
import { EmptyState, ErrorState } from '../components/ui';
import { ANNONCE_CATEGORIES, getAnnonceCategoryLabel } from '../constants';
import { formatPrice, calculateAge } from '../utils';
import annonceService from '../services/annonceService';
import AnnonceFormModal from '../modals/AnnonceFormModal';

// ── Helpers ──

const FEATURED_BADGES = {
  une: { label: 'annonces.featured.une', icon: Star, className: 'bg-amber-500 text-white' },
  urgent: { label: 'annonces.featured.urgent', icon: Zap, className: 'bg-red-500 text-white' },
};

const CATEGORY_COLORS = {
  commerce: 'from-indigo-400 to-blue-500',
  services: 'from-emerald-400 to-teal-500',
  logement: 'from-amber-400 to-orange-500',
  emploi: 'from-violet-400 to-purple-500',
  divers: 'from-gray-400 to-slate-500',
};

// ── Composant ──

const AnnoncesPage = ({ user, openAuth, toast }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const userAge = calculateAge(user?.dateOfBirth);
  const isAdult = userAge === null || userAge >= 18;
  const [annonces, setAnnonces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [searchText, setSearchText] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const debounceRef = useRef(null);
  const [formOpen, setFormOpen] = useState(false);

  // Debounce search 300ms
  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedSearch(searchText), 300);
    return () => clearTimeout(debounceRef.current);
  }, [searchText]);

  // Fetch annonces
  const fetchAnnonces = useCallback(async () => {
    try {
      setError(false);
      setLoading(true);
      const filters = {};
      if (categoryFilter) filters.category = categoryFilter;
      if (debouncedSearch) filters.search = debouncedSearch;
      const result = await annonceService.getAnnonces(filters);
      if (result.success) {
        setAnnonces(result.data);
      } else {
        setError(true);
      }
    } catch (_err) {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [categoryFilter, debouncedSearch]);

  useEffect(() => {
    fetchAnnonces();
  }, [fetchAnnonces]);

  const handlePublier = () => {
    if (!user) {
      openAuth();
      return;
    }
    setFormOpen(true);
  };

  // Formater le prix d'une annonce
  const displayPrice = (annonce) => {
    if (annonce.priceLabel === 'gratuit') return t('common.free');
    if (annonce.priceLabel === 'sur_devis') return t('common.onQuote');
    return formatPrice(annonce.price);
  };

  // Featured badge component
  const FeaturedBadge = ({ featured }) => {
    if (!featured) return null;
    const badge = FEATURED_BADGES[featured.type];
    if (!badge) return null;
    const Icon = badge.icon;
    return (
      <span
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold ${badge.className}`}
      >
        <Icon size={11} />
        {t(badge.label)}
      </span>
    );
  };

  return (
    <div className="filigrane-kotaw pt-24 pb-32 lg:pb-20 container mx-auto px-4 overflow-hidden">
      <Helmet>
        <title>{t('annonces.meta.title')}</title>
        <meta name="description" content={t('annonces.meta.description')} />
        <link rel="canonical" href="https://hmonghearts.com/annonces" />
        <meta property="og:title" content={t('annonces.meta.title')} />
        <meta property="og:description" content={t('annonces.meta.description')} />
        <meta property="og:image" content="https://hmonghearts.com/og-default.png" />
        <meta property="og:url" content="https://hmonghearts.com/annonces" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        {annonces.length > 0 && (
          <script type="application/ld+json">
            {JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'ItemList',
              name: 'Annonces de la communauté Hmong',
              numberOfItems: annonces.length,
              itemListElement: annonces.slice(0, 10).map((a, i) => ({
                '@type': 'ListItem',
                position: i + 1,
                url: `https://hmonghearts.com/annonces/${a.id}`,
              })),
            })}
          </script>
        )}
      </Helmet>
      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h1 className="text-lg sm:text-2xl font-bold dark:text-gray-100">
            {t('annonces.title')}
          </h1>
          <p className="text-xs sm:text-sm text-gray-400 dark:text-gray-400 mt-1">
            {t('annonces.subtitle')}
          </p>
        </div>
        {isAdult && (
          <button
            onClick={handlePublier}
            data-testid="annonces-btn-publier"
            className="flex-shrink-0 inline-flex items-center gap-1.5 text-xs sm:text-sm px-3 py-1.5 sm:px-4 sm:py-2 rounded-full bg-gradient-to-r from-rose-500 to-fuchsia-500 text-white font-medium whitespace-nowrap"
          >
            <Plus size={16} />
            {t('annonces.publish')}
          </button>
        )}
      </div>

      {/* Barre de recherche */}
      <div className="relative mb-4">
        <Search
          size={18}
          className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500"
        />
        <input
          type="text"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          placeholder={t('annonces.searchPlaceholder')}
          aria-label={t('annonces.searchLabel')}
          className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 text-sm dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-400 transition"
          data-testid="annonces-search-input"
        />
      </div>

      {/* Filtres catégorie */}
      <div className="flex gap-2 overflow-x-auto pb-1 mb-6" style={{ scrollbarWidth: 'none' }}>
        <button
          onClick={() => setCategoryFilter('')}
          className={`flex-shrink-0 px-2.5 py-1 rounded-full text-xs font-semibold transition ${
            categoryFilter === ''
              ? 'bg-indigo-500 text-white'
              : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
          }`}
          data-testid="annonces-filter-all"
        >
          {t('annonces.filters.all')}
        </button>
        {ANNONCE_CATEGORIES.map((cat) => (
          <button
            key={cat.id}
            onClick={() => setCategoryFilter(categoryFilter === cat.id ? '' : cat.id)}
            className={`flex-shrink-0 px-2.5 py-1 rounded-full text-xs font-semibold transition ${
              categoryFilter === cat.id
                ? 'bg-indigo-500 text-white'
                : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
            }`}
            data-testid={`annonces-filter-${cat.id}`}
          >
            {t(cat.labelKey)}
          </button>
        ))}
      </div>

      {/* Contenu */}
      {error ? (
        <ErrorState onRetry={fetchAnnonces} />
      ) : loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {[...Array(6)].map((_, i) => (
            <div
              key={i}
              className="rounded-xl overflow-hidden bg-white dark:bg-gray-900 shadow-sm animate-pulse"
            >
              <div className="h-40 bg-gray-200 dark:bg-gray-700" />
              <div className="p-4 space-y-3">
                <div className="h-5 w-3/4 bg-gray-200 dark:bg-gray-700 rounded-lg" />
                <div className="h-4 w-1/2 bg-gray-200 dark:bg-gray-700 rounded-lg" />
                <div className="h-3 w-28 bg-gray-200 dark:bg-gray-700 rounded-lg" />
              </div>
            </div>
          ))}
        </div>
      ) : annonces.length === 0 ? (
        <EmptyState
          icon={Megaphone}
          title={t('annonces.empty.title')}
          desc={
            debouncedSearch || categoryFilter
              ? t('annonces.empty.noMatch')
              : user && isAdult
                ? t('annonces.empty.authAdult')
                : user
                  ? t('annonces.empty.authMinor')
                  : t('annonces.empty.guest')
          }
          ctaLabel={
            debouncedSearch || categoryFilter
              ? t('annonces.cta.viewAll')
              : user && isAdult
                ? t('annonces.cta.publish')
                : user
                  ? null
                  : t('annonces.cta.join')
          }
          onCtaClick={
            debouncedSearch || categoryFilter
              ? () => {
                  setCategoryFilter('');
                  setSearchText('');
                }
              : user && isAdult
                ? handlePublier
                : user
                  ? null
                  : openAuth
          }
        />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {annonces.map((annonce, index) => (
            <button
              key={annonce.id}
              onClick={() => navigate(`/annonces/${annonce.id}`)}
              className="text-left rounded-xl overflow-hidden bg-white dark:bg-gray-900 shadow-sm hover:shadow-md transition-shadow animate-fade-in-up focus:outline-none focus:ring-2 focus:ring-indigo-500/30"
              style={{ animationDelay: `${index * 60}ms` }}
              data-testid="annonces-card"
            >
              {/* Image / Gradient fallback */}
              <div className="relative">
                {annonce.photos && annonce.photos.length > 0 ? (
                  <img
                    src={annonce.photos[0]}
                    alt={annonce.title}
                    className="w-full h-40 object-cover"
                    loading="lazy"
                  />
                ) : (
                  <div
                    className={`h-40 bg-gradient-to-br ${CATEGORY_COLORS[annonce.category] || 'from-gray-400 to-gray-500'} flex items-center justify-center`}
                  >
                    <Megaphone size={48} className="text-white/20" />
                  </div>
                )}
                <div className="absolute top-3 left-3">
                  <span className="inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold bg-white/90 dark:bg-gray-900/90 text-gray-700 dark:text-gray-300">
                    {t(getAnnonceCategoryLabel(annonce.category))}
                  </span>
                </div>
                {annonce.featured && (
                  <div className="absolute top-3 right-3">
                    <FeaturedBadge featured={annonce.featured} />
                  </div>
                )}
              </div>

              {/* Corps */}
              <div className="p-3 sm:p-4">
                <h3 className="font-medium sm:font-bold text-sm sm:text-lg mb-1 dark:text-gray-100 truncate sm:line-clamp-2 sm:whitespace-normal">
                  {annonce.title}
                </h3>

                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs sm:text-sm font-semibold text-indigo-600 dark:text-indigo-400">
                    {displayPrice(annonce)}
                  </span>
                </div>

                <div className="flex items-center gap-1.5 text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                  <MapPin size={14} className="flex-shrink-0" />
                  <span className="truncate">{annonce.city}</span>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Modal création */}
      <AnnonceFormModal
        isOpen={formOpen}
        onClose={() => setFormOpen(false)}
        user={user}
        toast={toast}
        annonce={null}
        onSuccess={fetchAnnonces}
      />
    </div>
  );
};

export default AnnoncesPage;
